import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Database } from "@/integrations/supabase/types";

type Lesson = Database["public"]["Tables"]["lessons"]["Row"] & {
  module_id: string;
};

type Module = {
  id: string;
  title: string;
  order_index: number;
  lessons: Lesson[];
};

type LessonProgress = {
  id: string;
  lesson_id: string;
  completed: boolean;
  last_position_seconds: number | null;
};

export const useLessonPlayer = (courseId: string, lessonId?: string) => {
  const [modules, setModules] = useState<Module[]>([]);
  const [currentLesson, setCurrentLesson] = useState<Lesson | null>(null);
  const [progress, setProgress] = useState<Record<string, LessonProgress>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isEnrolled, setIsEnrolled] = useState(false);

  useEffect(() => {
    fetchCourseData();
  }, [courseId]);

  useEffect(() => {
    if (lessonId && modules.length > 0) {
      selectLesson(lessonId);
    } else if (modules.length > 0 && !currentLesson) {
      // Auto-select first incomplete lesson or first lesson
      const firstIncomplete = findFirstIncompleteLesson();
      if (firstIncomplete) {
        selectLesson(firstIncomplete.id);
      }
    }
  }, [lessonId, modules]);

  const fetchCourseData = async () => {
    try {
      setLoading(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setIsEnrolled(false);
        setLoading(false);
        return;
      }

      // Check enrollment
      const { data: enrollment } = await supabase
        .from("enrollments")
        .select("id")
        .eq("user_id", user.id)
        .eq("course_id", courseId)
        .maybeSingle();

      if (!enrollment) {
        setIsEnrolled(false);
        setLoading(false);
        return;
      }

      setIsEnrolled(true);

      // Fetch modules with lessons
      const { data: modulesData, error: modulesError } = await supabase
        .from("modules")
        .select(`
          id,
          title,
          order_index,
          lessons(*)
        `)
        .eq("course_id", courseId)
        .order("order_index", { ascending: true });

      if (modulesError) throw modulesError;

      const sortedModules = modulesData?.map(module => ({
        ...module,
        lessons: (module.lessons as Lesson[]).sort((a, b) => a.order_index - b.order_index)
      })) || [];

      setModules(sortedModules);

      // Fetch lesson progress
      const lessonIds = sortedModules.flatMap(m => m.lessons.map(l => l.id));
      const { data: progressData } = await supabase
        .from("lesson_progress")
        .select("*")
        .eq("user_id", user.id)
        .in("lesson_id", lessonIds);

      const progressMap: Record<string, LessonProgress> = {};
      progressData?.forEach(p => {
        progressMap[p.lesson_id] = p;
      });
      setProgress(progressMap);

    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load course");
      console.error("Error fetching course data:", err);
    } finally {
      setLoading(false);
    }
  };

  const selectLesson = (lessonId: string) => {
    for (const module of modules) {
      const lesson = module.lessons.find(l => l.id === lessonId);
      if (lesson) {
        setCurrentLesson(lesson);
        return;
      }
    }
  };

  const findFirstIncompleteLesson = () => {
    for (const module of modules) {
      for (const lesson of module.lessons) {
        if (!progress[lesson.id]?.completed) {
          return lesson;
        }
      }
    }
    return modules[0]?.lessons[0] || null;
  };

  const markLessonComplete = async (lessonId: string) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error("Not authenticated");

      const existingProgress = progress[lessonId];

      if (existingProgress) {
        const { error } = await supabase
          .from("lesson_progress")
          .update({
            completed: true,
            completed_at: new Date().toISOString(),
          })
          .eq("id", existingProgress.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from("lesson_progress")
          .insert({
            user_id: user.id,
            lesson_id: lessonId,
            completed: true,
            completed_at: new Date().toISOString(),
          });

        if (error) throw error;
      }

      // Update local state
      setProgress(prev => ({
        ...prev,
        [lessonId]: {
          ...prev[lessonId],
          lesson_id: lessonId,
          completed: true,
        } as LessonProgress,
      }));

      // Update enrollment progress
      await updateEnrollmentProgress();

      return { success: true };
    } catch (err) {
      console.error("Error marking lesson complete:", err);
      return { success: false };
    }
  };

  const updateEnrollmentProgress = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const totalLessons = modules.reduce((sum, m) => sum + m.lessons.length, 0);
      const completedLessons = Object.values(progress).filter(p => p.completed).length;
      const progressPercent = totalLessons > 0 ? Math.round((completedLessons / totalLessons) * 100) : 0;

      await supabase
        .from("enrollments")
        .update({ progress: progressPercent })
        .eq("user_id", user.id)
        .eq("course_id", courseId);
    } catch (err) {
      console.error("Error updating enrollment progress:", err);
    }
  };

  const getNextLesson = () => {
    if (!currentLesson) return null;

    for (let i = 0; i < modules.length; i++) {
      const module = modules[i];
      const lessonIndex = module.lessons.findIndex(l => l.id === currentLesson.id);
      
      if (lessonIndex !== -1) {
        // Check if there's a next lesson in the same module
        if (lessonIndex < module.lessons.length - 1) {
          return module.lessons[lessonIndex + 1];
        }
        // Check if there's a next module
        if (i < modules.length - 1) {
          return modules[i + 1].lessons[0];
        }
      }
    }
    return null;
  };

  const getPreviousLesson = () => {
    if (!currentLesson) return null;

    for (let i = 0; i < modules.length; i++) {
      const module = modules[i];
      const lessonIndex = module.lessons.findIndex(l => l.id === currentLesson.id);
      
      if (lessonIndex !== -1) {
        // Check if there's a previous lesson in the same module
        if (lessonIndex > 0) {
          return module.lessons[lessonIndex - 1];
        }
        // Check if there's a previous module
        if (i > 0) {
          const prevModule = modules[i - 1];
          return prevModule.lessons[prevModule.lessons.length - 1];
        }
      }
    }
    return null;
  };

  const getCurrentLessonIndex = () => {
    let index = 0;
    for (const module of modules) {
      for (const lesson of module.lessons) {
        index++;
        if (lesson.id === currentLesson?.id) {
          return index;
        }
      }
    }
    return 0;
  };

  const getTotalLessons = () => {
    return modules.reduce((sum, m) => sum + m.lessons.length, 0);
  };

  return {
    modules,
    currentLesson,
    progress,
    loading,
    error,
    isEnrolled,
    selectLesson,
    markLessonComplete,
    getNextLesson,
    getPreviousLesson,
    getCurrentLessonIndex,
    getTotalLessons,
  };
};
